import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { useGetFile } from "src/shared/hooks";
import { HackerAPIFile } from "src/shared/utils/hackerapi";
import { MagnifyingGlassIcon } from "src/static/icons";

export interface ProfileOption {
  claimId: number;
  name: string;
  profilePhoto: HackerAPIFile;
  bio: string;
}

export interface ProfileTypeaheadProps
  extends React.ComponentPropsWithoutRef<"div"> {
  profileOptions: ProfileOption[];
  onSearchChange?: (value: string) => void;
  renderAction: (option: ProfileOption) => React.ReactNode;
  /** Contextual content above result rows (e.g. no-results, team-full). Shown even when options are empty. */
  resultsNotice?: React.ReactNode;
}

const SEARCH_PLACEHOLDER = "Search by name...";

// React 16 has no useId(); module counter keeps aria-controls ids unique across instances.
let nextResultsId = 0;

/**
 * Owns a single useGetFile so signed photo URIs stay fresh without batching
 * useGetFiles at the typeahead list layer.
 */
const ProfileOptionAvatar: React.FC<{
  profilePhoto: HackerAPIFile;
  name: string;
}> = ({ profilePhoto, name }) => {
  const photo = useGetFile(profilePhoto);

  if (!photo?.uri) {
    return <ProfileAvatarPlaceholder aria-hidden />;
  }

  return <ProfileAvatar src={photo.uri} alt={`${name}'s profile photo`} />;
};

const ProfileTypeahead: React.FC<ProfileTypeaheadProps> = ({
  profileOptions,
  onSearchChange,
  renderAction,
  resultsNotice,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resultsIdRef = useRef(`profile-typeahead-results-${nextResultsId++}`);
  const resultsId = resultsIdRef.current;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setIsOpen(true);
    onSearchChange?.(value);
  };

  return (
    <Container ref={containerRef} {...props}>
      <SearchFieldShell isOpen={isOpen}>
        <SearchField
          type="text"
          value={searchValue}
          placeholder={SEARCH_PLACEHOLDER}
          aria-label={SEARCH_PLACEHOLDER}
          aria-expanded={isOpen}
          aria-controls={resultsId}
          autoComplete="off"
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
        />
        <MagnifyingGlassIcon width={20} height={20} aria-hidden />
      </SearchFieldShell>

      {isOpen && (
        <Dropdown id={resultsId}>
          <MenuSection>
            {resultsNotice != null && (
              <ResultsNotice>{resultsNotice}</ResultsNotice>
            )}
            {profileOptions.map((option) => (
              <ResultRow key={option.claimId}>
                <ProfileOptionAvatar
                  profilePhoto={option.profilePhoto}
                  name={option.name}
                />
                <ProfileCopy>
                  <ProfileName>{option.name}</ProfileName>
                  {option.bio.trim() !== "" && (
                    <ProfileBio title={option.bio}>{option.bio}</ProfileBio>
                  )}
                </ProfileCopy>
                <ActionSlot>{renderAction(option)}</ActionSlot>
              </ResultRow>
            ))}
          </MenuSection>
        </Dropdown>
      )}
    </Container>
  );
};

export default ProfileTypeahead;

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SearchFieldShell = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  border: 1.5px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: ${({ isOpen }) => (isOpen ? "8px 8px 0 0" : "8px")};
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const SearchField = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 1.6em;
  letter-spacing: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};

  &::placeholder {
    color: ${({ theme }) => theme.globalConstants.color.textTertiary};
    font-weight: 500;
  }
`;

const Dropdown = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.globalConstants.color.white};
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-top: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0px 4px 6px -2px rgba(0, 0, 0, 0.05),
    0px 10px 15px -3px rgba(0, 0, 0, 0.1);
  max-height: 320px;
  overflow-y: auto;
`;

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 8px 0;
`;

const ResultsNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5em;
  text-align: center;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const ResultRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;

  &:hover {
    background: ${({ theme }) => theme.globalConstants.color.backgroundLight};
  }
`;

const ProfileAvatar = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 4px;
  object-fit: cover;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
`;

const ProfileAvatarPlaceholder = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 4px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
`;

const ProfileCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ProfileName = styled.span`
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5em;
  letter-spacing: 0;
  color: ${({ theme }) => theme.globalConstants.color.textBody};
`;

const ProfileBio = styled.span`
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  letter-spacing: 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionSlot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;
